import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/auth-provider';
import { useDsmContext } from '../../context/dsm-provider';
import { Product, Stage, Context, Attribute } from '../../types/domain.types';
import ConfirmationModal from '../../components/confirmation-modal';
import ApiTester from '../../components/api-tester';
import { StyledInputLabel, StyledDataGridPro } from '../../themes/styled-components';
import { DataGridPro, GridColDef, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from '@mui/x-data-grid-pro';
import { Api as ApiIcon, ExpandMore as ExpandMoreIcon, Refresh as RefreshIcon, Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { 
  Grid, Paper, Box, Typography, CircularProgress, Fab, IconButton,
  FormControl, InputLabel, Select, MenuItem, SelectChangeEvent,
  List, ListItem, ListItemButton, ListItemText,
  Accordion, AccordionSummary, AccordionDetails, 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Radio,  FormControlLabel, Switch
} from '@mui/material';

// Entity type for CRUD operations
type EntityType = 'product' | 'stage' | 'context' | 'attribute';

export default function DsmView() {
  
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [accordionExpanded, setAccordionExpanded] = useState(true);
  const [apiTesterOpen, setApiTesterOpen] = useState(false);
  
  // Shared modal states for all entity types
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentEntityType, setCurrentEntityType] = useState<EntityType>('stage');
  
  // Products
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [productToEdit, setProductToEdit] = useState<Product | null>(null);
  const [productFormData, setProductFormData] = useState({ 
    product: '', 
    description: '' 
  });
  
  // Stages
  const [stageToDelete, setStageToDelete] = useState<Stage | null>(null);
  const [stageToEdit, setStageToEdit] = useState<Stage | null>(null);
  const [stageFormData, setStageFormData] = useState({ 
    stage: '', 
    description: '', 
    product: '' 
  });
  
  // Contexts
  const [contextToDelete, setContextToDelete] = useState<Context | null>(null);
  const [contextToEdit, setContextToEdit] = useState<Context | null>(null);
  const [contextFormData, setContextFormData] = useState({ 
    context: '', 
    description: '', 
    product: '', 
    stage: '' 
  });
  
  // Attributes
  const [attributeToDelete, setAttributeToDelete] = useState<Attribute | null>(null);
  const [attributeToEdit, setAttributeToEdit] = useState<Attribute | null>(null);
  const [attributeFormData, setAttributeFormData] = useState({ 
    att_name: '', 
    order: '',
    att_type: '', 
    precision: '',
    description: '', 
    product: '', 
    stage: '', 
    context: '',
    dq_rule_set: ''
  });

  // State for draft changes modal
  const [draftModalOpen, setDraftModalOpen] = useState(false);
  const [draftChangesLoaded, setDraftChangesLoaded] = useState(false);

  const { 

    isReleaseMode, setIsReleaseMode,

    // Products
    productsState, 
    loadProducts, 
    selectedProduct, setSelectedProduct,
    createProduct, updateProduct, deleteProduct,
    
    // Stages
    stagesState,
    loadStages,
    selectedStage, setSelectedStage,
    filteredStages,
    createStage, updateStage, deleteStage,
    
    // Contexts
    contextsState,
    loadContexts,
    selectedContext, setSelectedContext,
    filteredContexts,
    createContext, updateContext, deleteContext,
    
    // Attributes
    attributesState,
    loadAttributes,
    filteredAttributes,
    createAttribute, updateAttribute, deleteAttribute,

    // Cache
    clearCaches,

    // Draft Changes
    draftChangesState, loadDraftChanges,

    // Admin UI Visibility
    isAdminUiVisible, setIsAdminUiVisible,

  } = useDsmContext();

  const isProductsLoading = productsState.status === 'loading' || isLoading;
  const isStagesLoading = stagesState.status === 'loading';
  const isContextsLoading = contextsState.status === 'loading';
  const isAttributesLoading = attributesState.status === 'loading';

  const attributesWithIds = filteredAttributes.map((attr, index) => ({
    ...attr,
    id: `${attr.product}_${attr.stage || ''}_${attr.context || ''}_${attr.att_name}_${index}`
  }));

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    if (draftModalOpen && selectedProduct && !draftChangesLoaded) {
      loadDraftChanges(selectedProduct.product).then(() => setDraftChangesLoaded(true));
    }
    if (!draftModalOpen && draftChangesLoaded) {
      setDraftChangesLoaded(false);
    }
  }, [draftModalOpen, selectedProduct, loadDraftChanges, draftChangesLoaded]);

  const handleFormChange = (
    event: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }> | SelectChangeEvent<string>,
    setFormData: React.Dispatch<React.SetStateAction<any>>
  ) => {
    let name: string | undefined;
    let value: any;

    if ('target' in event && event.target) {
      // For SelectChangeEvent and ChangeEvent
      name = (event.target as any).name;
      value = (event.target as any).value;
    }

    if (name) {
      setFormData((prev: any) => ({
        ...prev,
        [name as string]: value
      }));
    }
  };

  const handleOpenDeleteProductModal = (product: Product, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setProductToDelete(product);
    setCurrentEntityType('product');
    setDeleteModalOpen(true);
  };
  
  const handleOpenAddProductModal = () => {
    setProductToEdit(null);
    setProductFormData({
      product: '',
      description: ''
    });
    setCurrentEntityType('product');
    setEditModalOpen(true);
  };

  const handleOpenEditProductModal = (product: Product, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setProductToEdit(product);
    setProductFormData({
      product: product.product,
      description: product.description || ''
    });
    setCurrentEntityType('product');
    setEditModalOpen(true);
  };

  const handleSaveProduct = async () => {
    if (productToEdit) {
      await updateProduct({
        ...productFormData,
        product: productToEdit.product // Preserve original ID
      }, productToEdit.product);
    } else {
      await createProduct(productFormData);
    }
    
    setEditModalOpen(false);
    setProductToEdit(null);
  };

  const handleConfirmDeleteProduct = async () => {
    if (productToDelete) {
      await deleteProduct(productToDelete.product);
      setDeleteModalOpen(false);
      setProductToDelete(null);
    }
  };

  const handleOpenDeleteStageModal = (stage: Stage, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setStageToDelete(stage);
    setCurrentEntityType('stage');
    setDeleteModalOpen(true);
  };
  
  const handleOpenAddStageModal = () => {
    setStageToEdit(null);
    setStageFormData({
      stage: '',
      description: '',
      product: selectedProduct?.product || ''
    });
    setCurrentEntityType('stage');
    setEditModalOpen(true);
  };

  const handleOpenEditStageModal = (stage: Stage, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setStageToEdit(stage);
    setStageFormData({
      stage: stage.stage,
      description: stage.description || '',
      product: stage.product
    });
    setCurrentEntityType('stage');
    setEditModalOpen(true);
  };

  const handleSaveStage = async () => {
    if (stageToEdit) {
      await updateStage({
        ...stageFormData,
        stage: stageToEdit.stage // Preserve original ID
      }, stageToEdit.stage);
    } else {
      await createStage(stageFormData);
    }
    
    setEditModalOpen(false);
    setStageToEdit(null);
  };

  const handleConfirmDeleteStage = async () => {
    if (stageToDelete && selectedProduct) {
      await deleteStage(selectedProduct.product, stageToDelete.stage);
      setDeleteModalOpen(false);
      setStageToDelete(null);
    }
  };

  const handleOpenDeleteContextModal = (context: Context, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setContextToDelete(context);
    setCurrentEntityType('context');
    setDeleteModalOpen(true);
  };
  
  const handleOpenAddContextModal = () => {
    setContextToEdit(null);
    setContextFormData({
      context: '',
      description: '',
      product: selectedProduct?.product || '',
      stage: selectedStage?.stage || ''
    });
    setCurrentEntityType('context');
    setEditModalOpen(true);
  };

  const handleOpenEditContextModal = (context: Context, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setContextToEdit(context);
    setContextFormData({
      context: context.context,
      description: context.description || '',
      product: context.product,
      stage: context.stage
    });
    setCurrentEntityType('context');
    setEditModalOpen(true);
  };

  const handleSaveContext = async () => {
    if (contextToEdit) {
      await updateContext({
        ...contextFormData,
        context: contextToEdit.context // Preserve original ID
      }, contextToEdit.context);
    } else {
      await createContext(contextFormData);
    }
    
    setEditModalOpen(false);
    setContextToEdit(null);
  };

  const handleConfirmDeleteContext = async () => {
    if (contextToDelete && selectedProduct) {
      await deleteContext(selectedProduct.product, contextToDelete.context);
      setDeleteModalOpen(false);
      setContextToDelete(null);
    }
  };

  const handleOpenDeleteAttributeModal = (attribute: Attribute, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setAttributeToDelete(attribute);
    setCurrentEntityType('attribute');
    setDeleteModalOpen(true);
  };
  
  const handleOpenAddAttributeModal = () => {
    setAttributeToEdit(null);
    setAttributeFormData({
      att_name: '',
      order: '',
      att_type: '',
      precision: '',
      description: '',
      product: selectedProduct?.product || '',
      stage: selectedStage?.stage || '',
      context: selectedContext?.context || '',
      dq_rule_set: ''
    });
    setCurrentEntityType('attribute');
    setEditModalOpen(true);
  };

  const handleOpenEditAttributeModal = (attribute: Attribute, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setAttributeToEdit(attribute);
    setAttributeFormData({
      att_name: attribute.att_name,
      order: attribute.order !== undefined && attribute.order !== null ? String(attribute.order) : '',
      att_type: attribute.att_type,
      precision: attribute.precision !== undefined && attribute.precision !== null ? String(attribute.precision) : '',
      description: attribute.description || '',
      product: attribute.product,
      stage: attribute.stage,
      context: attribute.context,
      dq_rule_set: Array.isArray(attribute.dq_rule_set)
        ? attribute.dq_rule_set.join(' | ')
        : (attribute.dq_rule_set || '')
    });
    setCurrentEntityType('attribute');
    setEditModalOpen(true);
  };

  const handleSaveAttribute = async () => {
    if (attributeToEdit) {
      await updateAttribute({
        ...attributeFormData,
        order: attributeFormData.order ? parseInt(attributeFormData.order, 10) : undefined,
        precision: attributeFormData.precision ? parseInt(attributeFormData.precision, 10) : undefined,
        dq_rule_set: attributeFormData.dq_rule_set
          .split(/\||\n/)
          .map((rule) => rule.trim())
          .filter((rule) => rule)
          .join('|'),
        att_name: attributeToEdit.att_name
      }, attributeToEdit.att_name);
    } else {
      await createAttribute({
        ...attributeFormData,
        order: attributeFormData.order ? parseInt(attributeFormData.order, 10) : undefined,
        precision: attributeFormData.precision ? parseInt(attributeFormData.precision, 10) : undefined,
        dq_rule_set: attributeFormData.dq_rule_set
          .split(/\||\n/)
          .map((rule) => rule.trim())
          .filter((rule) => rule)
          .join('|')
      });
    }

    setEditModalOpen(false);
    setAttributeToEdit(null);
  };

  const handleConfirmDeleteAttribute = async () => {
    if (attributeToDelete && selectedProduct && selectedStage && selectedContext) {
      await deleteAttribute(
        selectedProduct.product,
        selectedStage.stage,
        selectedContext.context,
        attributeToDelete.att_name
      );
      setDeleteModalOpen(false);
      setAttributeToDelete(null);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setProductToDelete(null);
    setStageToDelete(null);
    setContextToDelete(null);
    setAttributeToDelete(null);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setProductToEdit(null);
    setStageToEdit(null);
    setContextToEdit(null);
    setAttributeToEdit(null);
  };

  const handleConfirmDelete = async () => {
    switch (currentEntityType) {
      case 'product':
        await handleConfirmDeleteProduct();
        break;
      case 'stage':
        await handleConfirmDeleteStage();
        break;
      case 'context':
        await handleConfirmDeleteContext();
        break;
      case 'attribute':
        await handleConfirmDeleteAttribute();
        break;
    }
  };

  const handleProductChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    
    if (value === 'none') {
      setSelectedProduct(null);
    } else {
      const product = productsState.status === 'success' 
        ? productsState.data.find(p => p.product === value) || null
        : null;
      
      setSelectedProduct(product);
    }
  };

  const handleStageSelect = (stage: Stage | null) => {
    setSelectedStage(stage);
  };

  const handleContextSelect = (context: Context | null) => {
    setSelectedContext(context);
  };

  const handleAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setAccordionExpanded(isExpanded);
  };

  const handleRefreshClick = () => {
    setIsLoading(true);    
    clearCaches();    
    loadProducts().then(() => {
      if (selectedProduct) {
        Promise.all([
          loadStages(selectedProduct.product, true),
          loadContexts(selectedProduct.product, true),
          loadAttributes(selectedProduct.product, true)
        ]).finally(() => {
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });
  };

  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsReleaseMode(event.target.checked);
  };

  const getDeleteEntityDetails = () => {
    switch (currentEntityType) {
      case 'product':
        return {
          name: 'product',
          value: productToDelete?.product || ''
        };
      case 'stage':
        return {
          name: 'stage',
          value: stageToDelete?.stage || ''
        };
      case 'context':
        return {
          name: 'context',
          value: contextToDelete?.context || ''
        };
      case 'attribute':
        return {
          name: 'attribute',
          value: attributeToDelete?.att_name || ''
        };
    }
  };

  const attributeColumns: Array<GridColDef> = [
    { 
      field: 'att_name', 
      headerName: 'Attribute', 
      flex: 0.3,
      resizable: true, 
      sortable: true
    },
    {
      field: 'order',
      headerName: 'Order',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      valueGetter: (params) => params.row.order ?? ''
    },
    { 
      field: 'att_type', 
      headerName: 'Type', 
      flex: 0.15,
      resizable: true, 
      sortable: true,
      valueGetter: (params) => {
        const type = params.row.att_type;
        const precision = params.row.precision;
        return precision ? `${type} (${precision})` : type;
      }
    },
    { 
      field: 'description', 
      headerName: 'Description', 
      flex: 0.4,
      resizable: true, 
      sortable: true
    },
    { 
      field: 'dq_rule_set', 
      headerName: 'Rules', 
      flex: 0.5,
      resizable: true, 
      sortable: true,
      valueGetter: (params) => {
        const rules = Array.isArray(params.row.dq_rule_set)
          ? params.row.dq_rule_set
          : (params.row.dq_rule_set || '').split(',');
        return rules.join(' | ');
      }
    },
    { 
      field: 'context', 
      headerName: 'Context', 
      flex: 0.3,
      resizable: true, 
      sortable: true
    },
 
    
    // { 
    //   field: 'stage', 
    //   headerName: 'Stage', 
    //   flex: 0.08,
    //   resizable: true, 
    //   sortable: true
    // },


    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.15,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {/* Conditionally render edit and delete buttons based on isReleaseMode */}
          {!isReleaseMode && (
            <>
              <IconButton
                size="small"
                onClick={(e) => handleOpenEditAttributeModal(params.row as Attribute, e as any)}
                sx={{ color: theme.palette.secondary.light }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => handleOpenDeleteAttributeModal(params.row as Attribute, e as any)}
                sx={{ color: theme.palette.secondary.light, ml: 0.5 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      )
    }
  ];

  const draftChangesColumns: GridColDef[] = [
    { field: 'stage', headerName: 'Stage', flex: 0.15 },
    { field: 'context', headerName: 'Context', flex: 0.15 },
    { field: 'att_name', headerName: 'Attribute', flex: 0.15 },
    { field: 'action', headerName: 'Action', flex: 0.15 },
    { field: 'version', headerName: 'Version', flex: 0.15 },
    { field: 'user', headerName: 'User', flex: 0.15 },
    { field: 'ts', headerName: 'Timestamp', flex: 0.3 },
    { 
      field: 'change', 
      headerName: 'Changed Fields', 
      flex: 1, 
      valueGetter: (params: any) =>
        Array.isArray(params.row.change)
          ? params.row.change.filter(
              (key: string) => key !== 'last_modified' && key !== 'last_modified_by'
            ).join(', ')
          : ''
    }
  ];

  const CustomFooter = (count: number) => {
    return (
      <Box sx={{ 
        height: 30, 
        backgroundColor: 'white',
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 16px',
        fontSize: '0.75rem',
        color: 'text.secondary'
      }}>
        <span>{count} attribute{count !== 1 ? 's' : ''} found</span>
        {!isReleaseMode && (
          <IconButton 
            size="small"
            onClick={handleOpenAddAttributeModal}
            disabled={
              !selectedProduct ||
              !selectedStage || !selectedStage.stage ||
              !selectedContext || !selectedContext.context
            }
            sx={{ color: theme.palette.secondary.light }}            
          >
          <AddIcon fontSize="small" />
        </IconButton>)}
      </Box>
    );
  };

  const CustomGridToolbar = () => {
    return (
      <GridToolbarContainer 
        sx={{ 
          padding: '8px 8px',
          display: 'flex',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(224, 224, 224, 1)',
          backgroundColor: 'white'
        }}
      >
        <GridToolbarQuickFilter 
          sx={{ 
            width: '300px',
            '& .MuiInputBase-root': {
              height: '36px'
            }
          }}
          placeholder="Search attributes..." 
          debounceMs={200}
        />
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <GridToolbarFilterButton />
        </Box>
      </GridToolbarContainer>
    );
  }
  
  const sortByOrderOrName = <T extends { order?: number }>(a: T, b: T, nameField: keyof T): number => {
    
    // First try to sort by order if available on both items
    if (typeof a.order === 'number' && typeof b.order === 'number') {
      return a.order - b.order;
    }
    // If only one has order, prioritize it
    if (typeof a.order === 'number') return -1;
    if (typeof b.order === 'number') return 1;
    
    // Otherwise sort alphabetically by name
    const aName = String(a[nameField]).toLowerCase();
    const bName = String(b[nameField]).toLowerCase();
    return aName.localeCompare(bName);
  };

  const contextDisplay = (context: string, stage: string, isReleaseMode: boolean): string => {
    const suffixToRemove = isReleaseMode ? '/release' : '/draft';
    const trimmedContext = context.replace(new RegExp(`${suffixToRemove}$`), '');
    return `${trimmedContext} (${stage})`;
  };

  const entityDetails = getDeleteEntityDetails();
  
  return (
    <Grid item xs={12} sx={{ position: 'relative', height: '100%', display: 'flex' }}>
      <Paper sx={{ 
        p: 2, 
        display: 'flex', 
        flexDirection: 'column', 
        width: '100%',
        height: '100%',
        overflow: 'hidden'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          
          {/* Page Title */}
          <Typography component="h1" variant="h6" >
            Dynamic Schema Manager (DSM)
          </Typography>

          {/* API Test button (hide in prod) */}
          <IconButton
            size="small"
            onClick={() => setApiTesterOpen(true)}
            title="API Tester"
            sx={{ color: theme.palette.primary.main }}
          >
            <ApiIcon fontSize="medium" />
          </IconButton>
        </Box>
        
        {/* Error handling for products */}
        {productsState.status === 'error' && (
          <Box sx={{ p: 2, bgcolor: '#fff4f4', borderRadius: 1, mb: 2 }}>
            <Typography color="error">Error: {productsState.error}</Typography>
          </Box>
        )}
        
        {/* Collapsible Accordion for Product, Stage and Context Lists */}
        <Accordion 
          expanded={accordionExpanded} 
          onChange={handleAccordionChange}
          disableGutters
          elevation={0}
          sx={{ 
            mb: 2,
            flexShrink: 0,
            // border: '1px solid',
            // borderColor: `${theme.palette.secondary.main}40`,
            borderRadius: 1,
            backgroundColor: `${theme.palette.background.default}`,
            '&.MuiAccordion-root': {
              overflow: 'hidden',
              '&:before': { display: 'none' }
            },
            '& .MuiCollapse-root': {
              transition: 'height 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms !important'
            },
            '& .MuiAccordionSummary-root': {
              borderBottom: 'none',
            },
            transition: 'none !important'
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'white' }}/>}
            aria-controls="selection-panel-content"
            id="selection-panel-header"
            sx={{ 
              bgcolor: `${theme.palette.secondary.main}`, // Solid background
              borderRadius: '3px 3px 0 0',
              minHeight: 42,
              // Add solid border top, left, and right to override parent's transparent border
              // borderTop: `1px solid ${theme.palette.secondary.main}`,
              // borderLeft: `1px solid ${theme.palette.secondary.main}`,
              // borderRight: `1px solid ${theme.palette.secondary.main}`,
              // Negative margin to compensate for the parent's border
              // marginTop: '-1px',
              // marginLeft: '-1px',
              // marginRight: '-1px',
              '& .MuiAccordionSummary-content': {
                margin: '8px 0'
              }
            }}
          >
            <Typography sx={{ flexGrow: 1, color: theme.palette.primary.contrastText, letterSpacing: '.04em' }}>
              Product Selection & Filtering
              {(isProductsLoading || isStagesLoading || isContextsLoading) && (
                <CircularProgress size={16} sx={{ ml: 1, verticalAlign: 'middle' }} />
              )}
            </Typography>
          </AccordionSummary>  

          <AccordionDetails 
            sx={{ p: 2, pt: 2, pb: 2, backgroundColor: 'rgba(146, 0, 80, 0.01)' 
            }}>

            {/* Product Selection */}
            <Box sx={{ mb: 3, flexShrink: 0 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 1 
              }}>
                <StyledInputLabel id="product-select-label">Product</StyledInputLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {/* Show Draft Changes Button (only in draft mode) */}
                  {!isReleaseMode && (
                    <Button
                      variant="outlined"
                      color="inherit"
                      sx={{
                        mr: 2,
                        height: 32,
                        color: 'secondary.light',
                        borderColor: 'secondary.light',
                        backgroundColor: 'rgba(146, 0, 80, 0.01)',
                        '&:hover': {
                          backgroundColor: 'secondary.light',
                          color: 'white',
                          borderColor: 'secondary.light',
                        },
                      }}
                      onClick={() => setDraftModalOpen(true)}
                    >
                      Show Draft Changes
                    </Button>
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    Draft
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isReleaseMode}
                        onChange={handleModeChange}
                        inputProps={{ 'aria-label': 'release mode switch' }}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: 'secondary.light',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: 'secondary.light',
                          },
                        }}
                      />
                    }
                    label=""
                    sx={{ margin: 0 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    Release
                  </Typography>
                </Box>
              </Box>
              <FormControl size="small" sx={{ width: '100%' }}>
                <Select
                  labelId="product-select-label"
                  id="product-select"
                  value={selectedProduct?.product || 'none'}
                  onChange={handleProductChange}
                  disabled={isProductsLoading}
                  displayEmpty
                  sx={{ 
                    bgcolor: 'white',
                    '& .MuiSelect-select': { bgcolor: 'white' },
                  }}
                >
                  <MenuItem value="none">
                    <em>None</em>
                  </MenuItem>
                  {productsState.status === 'success' && productsState.data.map((product) => (
                    <MenuItem key={product.product} value={product.product}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <span>
                          {product.product.toUpperCase()}
                          {product.description ? ` (${product.description})` : ''}
                        </span>
                        
                        {isAdminUiVisible && (
                          <Box>                          
                            <IconButton 
                              size="small"
                              onClick={(e) => handleOpenEditProductModal(product, e)}
                              sx={{ color: theme.palette.primary.main, opacity: '1 !important' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small"
                              onClick={(e) => handleOpenDeleteProductModal(product, e)}
                              sx={{ ml: 0.5, color: theme.palette.primary.main, opacity: '1 !important' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            {/* Stage and Context Lists */}
            <Box sx={{ 
              display: 'flex', 
              gap: 3, 
              height: '200px',
            }}>

              {/* Stage List (Left) */}
              <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 1,
                  justifyContent: 'space-between' 
                }}>
                  <StyledInputLabel>Stages</StyledInputLabel>
                  {isStagesLoading && <CircularProgress size={16} />}
                  {isAdminUiVisible && (
                    <IconButton
                      size="small" 
                      onClick={handleOpenAddStageModal}
                      disabled={!selectedProduct}
                      sx={{ ml: 1, color: theme.palette.secondary.light, opacity: '1 !important' }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                
                <Box sx={{ 
                  border: '1px solid #ddd', 
                  borderRadius: 1, 
                  bgcolor: 'background.paper',
                  height: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}>

                  {/* Stage List */}
                  <List 
                    sx={{ 
                      overflow: 'auto',
                      flexGrow: 1,
                      p: 0
                    }}
                    dense
                  >
                    {/* None option with radio style */}
                    <ListItem 
                      disablePadding 
                      divider
                    >
                      <ListItemButton 
                        selected={selectedStage === null} 
                        onClick={() => handleStageSelect(null)}
                        disabled={!selectedProduct}
                        sx={{}}
                      >
                        <Radio 
                          checked={selectedStage === null}
                          size="small"
                          sx={{ 
                            padding: '4px 8px 4px 0',
                            color: 'rgba(0, 0, 0, 0.54)',
                            '&.Mui-checked': {
                              color: theme.palette.secondary.light
                            }
                          }}
                        />
                        <ListItemText primary="None" />
                      </ListItemButton>
                    </ListItem>
                    
                    {/* Stage items with radio style */}
                    {[...filteredStages]
                      .sort((a, b) => sortByOrderOrName(a, b, 'stage'))
                      .map((stage) => (
                        <ListItem 
                          key={`${stage.product}-${stage.stage}`}
                          disablePadding 
                          divider
                        >
                          <ListItemButton 
                            selected={selectedStage?.stage === stage.stage}
                            onClick={() => handleStageSelect(stage)}
                            sx={{ }}
                          >
                            <Radio 
                              checked={selectedStage?.stage === stage.stage}
                              size="small"
                              sx={{ 
                                padding: '4px 8px 4px 0',
                                color: 'rgba(0, 0, 0, 0.54)',
                                '&.Mui-checked': {
                                  color: theme.palette.secondary.light
                                }
                              }}
                            />
                            <ListItemText 
                              primary={`${stage.stage}`} 
                              primaryTypographyProps={{
                                noWrap: true,
                                title: stage.stage
                              }}

                            />
                            {isAdminUiVisible && (
                              <Box sx={{ ml: 'auto', display: 'flex' }}>
                                <IconButton 
                                  edge="end" 
                                  aria-label="edit" 
                                  size="small"
                                  sx={{ color: theme.palette.primary.main, opacity: '1 !important' }}
                                  onClick={(e) => handleOpenEditStageModal(stage, e)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  edge="end" 
                                  aria-label="delete" 
                                  size="small"
                                  onClick={(e) => handleOpenDeleteStageModal(stage, e)}
                                  sx={{ ml: 0.5, color: theme.palette.secondary.light, opacity: '1 !important' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>)}
                          </ListItemButton>
                        </ListItem>
                    ))}
                    
                    {/* Empty state */}
                    {selectedProduct && filteredStages.length === 0 && !isStagesLoading && (
                      <ListItem>
                        <ListItemText primary="No stages found" />
                      </ListItem>
                    )}
                  </List>

                </Box>
              </Box>
              
              {/* Context List (Right) */}
              <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 1,
                  justifyContent: 'space-between'
                }}>
                  <StyledInputLabel>Contexts</StyledInputLabel>
                  {isContextsLoading && <CircularProgress size={16} />}
                  {isAdminUiVisible && (<IconButton 
                    size="small" 
                    onClick={handleOpenAddContextModal}
                    disabled={!selectedProduct}
                    sx={{ ml: 1, color: theme.palette.secondary.light, opacity: '1 !important' }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>)}
                </Box>
                
                <Box sx={{ 
                  border: '1px solid #ddd', 
                  borderRadius: 1, 
                  bgcolor: 'background.paper',
                  height: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}>

                  {/* Context list*/}
                  <List 
                    sx={{ 
                      overflow: 'auto',
                      flexGrow: 1,
                      p: 0
                    }}
                    dense
                  >
                    {/* None option with radio style */}
                    <ListItem 
                      disablePadding 
                      divider
                    >
                      <ListItemButton 
                        selected={selectedContext === null} 
                        onClick={() => handleContextSelect(null)}
                        disabled={!selectedProduct}
                        sx={{ }}
                      >
                        <Radio 
                          checked={selectedContext === null}
                          size="small"
                          sx={{ 
                            padding: '4px 8px 4px 0',
                            color: 'rgba(0, 0, 0, 0.54)',
                            '&.Mui-checked': {
                              color: theme.palette.secondary.light
                            }
                          }}
                        />
                        <ListItemText primary="None" />
                      </ListItemButton>
                    </ListItem>
                    
                    {/* Context items with radio style */}
                    {[...filteredContexts]
                      .sort((a, b) => sortByOrderOrName(a, b, 'context'))
                      .map((context) => (
                        <ListItem 
                          key={`${context.product}-${context.stage}-${context.context}`}
                          disablePadding 
                          divider
                        >
                          <ListItemButton 
                            selected={selectedContext?.context === context.context}
                            onClick={() => handleContextSelect(context)}
                            sx={{ }}
                          >
                            <Radio 
                              checked={selectedContext?.context === context.context}
                              size="small"
                              sx={{ 
                                padding: '4px 8px 4px 0',
                                color: 'rgba(0, 0, 0, 0.54)',
                                '&.Mui-checked': {
                                  color: theme.palette.secondary.light
                                }
                              }}
                            />
                            <ListItemText 
                              primary={contextDisplay(context.context, context.stage, isReleaseMode)} 
                              primaryTypographyProps={{
                                noWrap: true,
                                title: contextDisplay(context.context, context.stage, isReleaseMode)
                              }}

                            />
                            {isAdminUiVisible && (
                              <Box sx={{ ml: 'auto', display: 'flex' }}>
                                <IconButton 
                                  edge="end" 
                                  aria-label="edit" 
                                  size="small"
                                  sx={{ color: theme.palette.primary.main, opacity: '1 !important' }}
                                  onClick={(e) => handleOpenEditContextModal(context, e)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  edge="end" 
                                  aria-label="delete" 
                                  size="small"
                                  onClick={(e) => handleOpenDeleteContextModal(context, e)}
                                  sx={{ ml: 0.5, color: theme.palette.secondary.light, opacity: '1 !important' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>)}
                          </ListItemButton>
                        </ListItem>
                    ))}
                    
                    {/* Empty state */}
                    {selectedProduct && filteredContexts.length === 0 && !isContextsLoading && (
                      <ListItem>
                        <ListItemText primary="No contexts found" />
                      </ListItem>
                    )}
                  </List>
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        
        {/* Attributes DataGrid */}
        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #ddd',
          borderRadius: 1,
          bgcolor: 'background.paper',
          overflow: 'hidden'
        }}>
          {isAttributesLoading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%' 
            }}>
              <CircularProgress />
            </Box>
          ) : filteredAttributes.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%', 
              color: 'text.secondary' 
            }}>
              <Typography>
                {selectedProduct 
                  ? 'No attributes found for the selected criteria'
                  : 'Select a product to view attributes'}
              </Typography>
              {selectedProduct && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddAttributeModal}
                  sx={{ mt: 2, bgcolor: 'white' }}
                  disabled={
                    !selectedProduct ||
                    !selectedStage || !selectedStage.stage ||
                    !selectedContext || !selectedContext.context
                  }
                >
                  Add Attribute
                </Button>
              )}
            </Box>
          ) : (
            <StyledDataGridPro
              columns={attributeColumns}
              rows={attributesWithIds}
              rowThreshold={0}
              disableDensitySelector={true}
              disableColumnResize={false}
              disableColumnSelector={true}
              disableRowSelectionOnClick={true}
              disableMultipleRowSelection={true}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'att_name', sort: 'asc' }],
                },
              }}
              slots={{ 
                toolbar: CustomGridToolbar,
                footer: () => CustomFooter(filteredAttributes.length)
              }}
              slotProps={{
                toolbar: {
                  // sx: { backgroundColor: 'white' },
                  // showQuickFilter: true,
                  // printOptions: { disableToolbarButton: true },
                  // csvOptions: { disableToolbarButton: true }
                }
              }}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
              }
              sx={{
                height: '100%',
                width: '100%',
                border: 'none',
                '& .MuiDataGrid-root': {
                  color: theme.palette.text.primary,
                  fontSize: '0.8rem'
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  fontSize: '.8rem'
                },
                '& .MuiDataGrid-cell': {
                  backgroundColor: 'white',
                  color: theme.palette.text.primary,
                  fontSize: '0.8rem'
                }
              }}
            />
          )}
        </Box>
        
        {/* Show selection summary at the bottom when accordion is collapsed */}
        {!accordionExpanded && (
          <Box sx={{ mt: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
            <Typography variant="body2">
              Selected: {selectedProduct ? `Product: ${selectedProduct.product}` : ''}
              {selectedProduct && selectedStage ? ' • ' : ''}
              {selectedStage ? `Stage: ${selectedStage.stage}` : ''}
              {(selectedProduct || selectedStage) && selectedContext ? ' • ' : ''}
              {selectedContext ? `Context: ${selectedContext.context}` : ''}
            </Typography>
          </Box>
        )}

        {/* Refresh button */}
        <Fab 
          color="primary" 
          aria-label="refresh" 
          onClick={handleRefreshClick}
          sx={{ 
            position: 'absolute', 
            bottom: '40px', 
            right: '145px',
            boxShadow: 3
          }}
        >
          <RefreshIcon />
        </Fab>

        {/* Shared confirmation modal for all entity types */}
        <ConfirmationModal
          open={deleteModalOpen}
          title={`Delete ${currentEntityType}`}
          message={`Are you sure you want to delete this ${entityDetails.name}${entityDetails.value ? ` "${entityDetails.value}"` : ''}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={handleCloseDeleteModal}
        />

        {/* Dynamic form modal based on current entity type */}
        <Dialog
          open={editModalOpen}
          onClose={handleCloseEditModal}
          aria-labelledby="form-dialog-title"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="form-dialog-title">
            {currentEntityType === 'product' && (productToEdit ? 'Edit Product' : 'Add New Product')}
            {currentEntityType === 'stage' && (stageToEdit ? 'Edit Stage' : 'Add New Stage')}
            {currentEntityType === 'context' && (contextToEdit ? 'Edit Context' : 'Add New Context')}
            {currentEntityType === 'attribute' && (attributeToEdit ? 'Edit Attribute' : 'Add New Attribute')}
          </DialogTitle>
          <DialogContent>
            {/* Product Form */}
            {currentEntityType === 'product' && (
              <>
                <TextField
                  autoFocus
                  margin="dense"
                  id="product"
                  name="product"
                  label="Product ID"
                  type="text"
                  fullWidth
                  value={productFormData.product}
                  onChange={(e) => handleFormChange(e, setProductFormData)}
                  disabled={!!productToEdit}
                  sx={{ mb: 2 }}
                />
                <TextField
                  margin="dense"
                  id="description"
                  name="description"
                  label="Description"
                  type="text"
                  fullWidth
                  multiline
                  rows={3}
                  value={productFormData.description}
                  onChange={(e) => handleFormChange(e, setProductFormData)}
                />
              </>
            )}

            {/* Stage Form */}
            {currentEntityType === 'stage' && (
              <>
                <TextField
                  autoFocus
                  margin="dense"
                  id="stage"
                  name="stage"
                  label="Stage ID"
                  type="text"
                  fullWidth
                  value={stageFormData.stage}
                  onChange={(e) => handleFormChange(e, setStageFormData)}
                  disabled={!!stageToEdit}
                  sx={{ mb: 2 }}
                />
                <TextField
                  margin="dense"
                  id="description"
                  name="description"
                  label="Description"
                  type="text"
                  fullWidth
                  multiline
                  rows={3}
                  value={stageFormData.description}
                  onChange={(e) => handleFormChange(e, setStageFormData)}
                />
              </>
            )}

            {/* Context Form */}
            {currentEntityType === 'context' && (
              <>
                <TextField
                  autoFocus
                  margin="dense"
                  id="context"
                  name="context"
                  label="Context ID"
                  type="text"
                  fullWidth
                  value={contextFormData.context}
                  onChange={(e) => handleFormChange(e, setContextFormData)}
                  disabled={!!contextToEdit}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                  <InputLabel id="stage-select-label">Stage</InputLabel>
                  <Select
                    labelId="stage-select-label"
                    id="stage"
                    name="stage"
                    value={contextFormData.stage}
                    label="Stage"
                    disabled={!!contextToEdit}
                  >
                    {filteredStages.map((stage) => (
                      <MenuItem key={stage.stage} value={stage.stage}>
                        {stage.stage}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  margin="dense"
                  id="description"
                  name="description"
                  label="Description"
                  type="text"
                  fullWidth
                  multiline
                  rows={3}
                  value={contextFormData.description}
                  onChange={(e) => handleFormChange(e, setContextFormData)}
                />
              </>
            )}

            {/* Attribute Form */}
            {currentEntityType === 'attribute' && (
              <>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    autoFocus
                    margin="dense"
                    id="att_name"
                    name="att_name"
                    label="Attribute Name"
                    type="text"
                    fullWidth
                    value={attributeFormData.att_name}
                    onChange={(e) => handleFormChange(e, setAttributeFormData)}
                    disabled={!!attributeToEdit}
                    sx={{ flex: 7 }}
                  />
                  <TextField
                    margin="dense"
                    id="order"
                    name="order"
                    label="Order"
                    type="text"
                    fullWidth
                    value={attributeFormData.order}
                    onChange={(e) => handleFormChange(e, setAttributeFormData)}
                    sx={{ flex: 3 }}
                    error={!!attributeFormData.order && !/^\d+$/.test(attributeFormData.order)}
                    helperText={!!attributeFormData.order && !/^\d+$/.test(attributeFormData.order) ? 'Order must be an integer' : ' '}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <FormControl fullWidth margin="dense" sx={{ flex: 7 }}>
                    <InputLabel id="att_type-select-label">Type</InputLabel>
                    <Select
                      labelId="att_type-select-label"
                      id="att_type"
                      name="att_type"
                      value={attributeFormData.att_type}
                      onChange={(e) => {
                        const { value } = e.target;
                        setAttributeFormData((prev) => ({
                          ...prev,
                          att_type: value,
                          precision: value === 'str' || value === 'float' ? prev.precision : '',
                        }));
                      }}
                      label="Type"
                    >
                      <MenuItem value="str">str</MenuItem>
                      <MenuItem value="int">int</MenuItem>
                      <MenuItem value="float">float</MenuItem>
                      <MenuItem value="bool">bool</MenuItem>
                      <MenuItem value="datetime">datetime</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    margin="dense"
                    id="precision"
                    name="precision"
                    label="Precision"
                    type="text"
                    fullWidth
                    value={attributeFormData.precision}
                    onChange={(e) => handleFormChange(e, setAttributeFormData)}
                    sx={{ flex: 3 }}
                    error={!!attributeFormData.precision && !/^\d+$/.test(attributeFormData.precision)}
                    helperText={!!attributeFormData.precision && !/^\d+$/.test(attributeFormData.precision) ? 'Precision must be an integer' : ' '}
                    disabled={attributeFormData.att_type !== 'str' && attributeFormData.att_type !== 'float'}
                  />
                </Box>
                <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                  <InputLabel id="stage-select-label">Stage</InputLabel>
                  <Select
                    labelId="stage-select-label"
                    id="stage"
                    name="stage"
                    value={attributeFormData.stage}
                    label="Stage"
                    disabled
                  >
                    <MenuItem value="">None</MenuItem>
                    {filteredStages.map((stage) => (
                      <MenuItem key={stage.stage} value={stage.stage}>
                        {stage.stage}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                  <InputLabel id="context-select-label">Context</InputLabel>
                  <Select
                    labelId="context-select-label"
                    id="context"
                    name="context"
                    value={attributeFormData.context}
                    label="Context"
                    disabled
                  >
                    <MenuItem value="">None</MenuItem>
                    {filteredContexts.map((context) => (
                      <MenuItem key={context.context} value={context.context}>
                        {context.context}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  margin="dense"
                  id="dq_rule_set"
                  name="dq_rule_set"
                  label="Data Quality Rules"
                  type="text"
                  fullWidth
                  multiline
                  rows={2}
                  value={Array.isArray(attributeFormData.dq_rule_set) ? attributeFormData.dq_rule_set.join('\n') : attributeFormData.dq_rule_set}
                  onChange={(e) => handleFormChange(e, setAttributeFormData)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  margin="dense"
                  id="description"
                  name="description"
                  label="Description"
                  type="text"
                  fullWidth
                  multiline
                  rows={2}
                  value={attributeFormData.description}
                  onChange={(e) => handleFormChange(e, setAttributeFormData)}
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditModal} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={() => {
                switch (currentEntityType) {
                  case 'product':
                    handleSaveProduct();
                    break;
                  case 'stage':
                    handleSaveStage();
                    break;
                  case 'context':
                    handleSaveContext();
                    break;
                  case 'attribute':
                    handleSaveAttribute();
                    break;
                }
              }} 
              color="primary" 
              variant="contained"
              disabled={
                (currentEntityType === 'product' && !productFormData.product.trim()) ||
                (currentEntityType === 'stage' && !stageFormData.stage.trim()) ||
                (currentEntityType === 'context' && (!contextFormData.context.trim() || !contextFormData.stage)) ||
                (currentEntityType === 'attribute' && (
                  !attributeFormData.att_name.trim() ||
                  !attributeFormData.att_type ||
                  (!!attributeFormData.order && !/^\d+$/.test(attributeFormData.order)) ||
                  (!!attributeFormData.precision && !/^\d+$/.test(attributeFormData.precision))
                ))
              }
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* API tester (hide in prod) */}
        <ApiTester 
          open={apiTesterOpen}
          onClose={() => setApiTesterOpen(false)}
          baseUrl={process.env.REACT_APP_DSM_API_URL || '/api'} 
          title="DSM API Tester"
        />

        {/* Draft Changes Modal */}
        <Dialog
          open={draftModalOpen}
          onClose={() => setDraftModalOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Draft Changes</DialogTitle>
          <DialogContent>
            <Box sx={{ height: 500, width: '100%' }}>
              {draftChangesState.status === 'loading' && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              )}
              {draftChangesState.status === 'error' && (
                <Typography color="error">{draftChangesState.error}</Typography>
              )}
              {draftChangesState.status === 'success' && (
                <DataGridPro
                  columns={draftChangesColumns}
                  rows={draftChangesState.data?.map((row, idx) => ({ ...row, id: idx })) || []}
                  autoHeight={false}
                  disableRowSelectionOnClick
                  sx={{
                    bgcolor: 'white',
                    borderRadius: 1,
                    fontSize: '0.85rem',
                    minHeight: 400,
                    '& .MuiDataGrid-columnHeader': { backgroundColor: '#f5f5f5' }
                  }}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDraftModalOpen(false)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>



      </Paper>
    </Grid>
  );
}
